<template>
  <div>
    <tableComponent
      ref="tableComponent"
      :tableSettings="tableSettings"
      :tableData="tableData"
      :headers="headers"
      @newDocument="newDocument"
      @reloadData="reloadDataFunc"
      @openMultiUserDialog="openMultiUserDialog"
      @openBulkMove="openBulkMove"
      @confirmBulkDelete="confirmBulkDelete"
    />
    <moveDialog
      :item="bulkMove.item"
      :moveDialog="bulkMove.dialog"
      :loading="bulkMove.loading"
      :array="bulkMove.targets"
      @cancel="bulkMove.dialog.open = false"
      @confirm="handleBulkMoveConfirm"
    />
    <v-dialog v-model="bulkConfirm.open" max-width="440">
      <v-card>
        <v-card-title>{{ bulkConfirm.title }}</v-card-title>
        <v-card-text>{{ bulkConfirm.message }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="bulkConfirm.open = false" :disabled="bulkConfirm.loading">
            {{ $t("general.cancel") }}
          </v-btn>
          <v-btn color="error" :loading="bulkConfirm.loading" @click="runBulkDelete">
            {{ $t("inspections.bulk.deleteButton") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <multiUserDialog
      v-model="multiUserOpen"
      entityType="inspection"
      :availableItems="transferItems"
      itemText="title"
      itemValue="_id"
      :clients="transferClients"
      :currentClientId="currentClientId"
      @confirm="executeTransfer"
      entityLabel="multiUser.dialog.inspections"
    />
  </div>
</template>
<script>
import { getUniqueId } from "../../components/api/api";
import tableComponent from "../../components/_utils/dataTableHelpers/tableComponent.vue";
import multiUserDialog from "../../components/_utils/dataTableHelpers/actions/multiUserDialog.vue";
import moveDialog from "../../components/_utils/dataTableHelpers/actions/moveDialog.vue";

export default {
  components: {
    tableComponent,
    multiUserDialog,
    moveDialog,
  },
  data: () => ({
    tableSettings: {
      title: "",
      showSelect: true,
      showHeaderToolbar: true,
      headerButtons: [
        {
          title: "multiUser.button",
          color: "primary",
          action: "openMultiUserDialog",
          icon: "mdi-swap-horizontal",
          iconDark: true,
          dark: true,
        },
        {
          title: "inspections.addInspection",
          color: "secondary",
          action: "newDocument",
          icon: "add",
          iconDark: true,
          dark: true,
          outlined: true,
        },
      ],
      multipleSelectButtons: [
        {
          title: "inspections.bulk.moveButton",
          color: "primary",
          action: "openBulkMove",
          icon: "content_paste_go",
          iconDark: true,
          dark: true,
          outlined: true,
        },
        {
          title: "inspections.bulk.deleteButton",
          color: "error",
          action: "confirmBulkDelete",
          icon: "delete",
          iconDark: true,
          dark: true,
          outlined: true,
        },
      ],
    },
    bulkMove: {
      item: {},
      loading: false,
      targets: [],
      ids: [],
      dialog: {
        open: false,
        title: "inspections.bulk.moveTitle",
        description: "",
      },
    },
    bulkConfirm: {
      open: false,
      loading: false,
      title: "",
      message: "",
      ids: [],
    },
    tableData: {
      tableComponentSlots: [
        {
          id: getUniqueId(),
          slotName: "item.inspectionResponsible",
          componentName: "inspectionResponsible",
        },
        {
          id: getUniqueId(),
          slotName: "item.formattedInspectionStatus",
          componentName: "formattedInspectionStatus",
        },
        {
          id: getUniqueId(),
          slotName: "item.formattedInspectionDate",
          componentName: "formattedInspectionDate",
        },
        {
          id: getUniqueId(),
          slotName: "item.inspectionHighRiskCount",
          componentName: "inspectionHighRiskCount",
        },
        {
          id: getUniqueId(),
          slotName: "item.inspectionAction",
          componentName: "inspectionAction",
        },
      ],
      itemKey: "_id",
      uriPartPrepend: "inspection",
      uriPartAppend: "",
      populate: ["areas.tasks.ticket", "responsiblePerson"],
      sort: "-createdAt",
      editFormPushName: "inspectionForm",
    },
    currentClientId: "",
    isMultiUser: false,
    multiUserOpen: false,
    transferClients: [],
    transferItems: [],
  }),
  async created() {
    const categoryId = this.$route.params.inspectionCategoryId;
    if (categoryId) {
      this.tableData.uriPartAppend = `&category=${categoryId}`;
    }

    const user = await this.$store.dispatch("getUserInfo");
    this.currentClientId = user?.client?._id || "";
    this.isMultiUser = user?.role === "MultiUser";

    const res = await this.$store.dispatch("getAllInspections");
    this.transferItems = res.data || res;

    if (!this.isMultiUser) {
      this.tableSettings.headerButtons =
        this.tableSettings.headerButtons.filter(
          (btn) => btn.action !== "openMultiUserDialog",
        );
    }
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t("inspections.tableHeader.status"),
          value: "formattedInspectionStatus",
          align: "left",
        },
        {
          text: this.$t("inspections.tableHeader.inspection"),
          value: "title",
        },
        {
          text: this.$t("inspections.tableHeader.responsiblePerson"),
          value: "inspectionResponsible",
          sortable: false,
        },
        {
          text: this.$t("inspections.tableHeader.inspectionDate"),
          value: "formattedInspectionDate",
        },
        {
          text: this.$t("inspections.tableHeader.topicCount"),
          value: "areas.length",
        },
        {
          text: this.$t("riskAssessment.tableHeader.highRiskCount"),
          value: "inspectionHighRiskCount",
          sortable: false,
        },
        {
          text: "",
          value: "inspectionAction",
          align: "right",
          sortable: false,
        },
      ];
    },
  },
  methods: {
    reloadDataFunc() {
      this.$refs.tableComponent.getData();
    },
    async openBulkMove(selectedItems) {
      this.bulkMove.ids = (selectedItems || []).map((i) => i._id);
      const res = await this.$store.dispatch("getAllInspectionCategories");
      const cats = (res && res.data) || res || [];
      this.bulkMove.targets = cats
        .map((c) => ({ id: c.id || c._id, name: c.name }))
        .filter(
          (t) =>
            String(t.id) !==
            String(this.$route.params.inspectionCategoryId),
        );
      this.bulkMove.dialog.description = this.$t("inspections.bulk.moveText", {
        count: this.bulkMove.ids.length,
      });
      this.bulkMove.dialog.open = true;
    },
    async handleBulkMoveConfirm(targetId) {
      try {
        this.bulkMove.loading = true;
        await this.$store.dispatch("bulkMoveInspections", {
          ids: this.bulkMove.ids,
          category: targetId,
        });
        this.bulkMove.dialog.open = false;
        this.onBulkActionDone();
      } finally {
        this.bulkMove.loading = false;
      }
    },
    confirmBulkDelete(selectedItems) {
      const ids = (selectedItems || []).map((i) => i._id);
      this.bulkConfirm = {
        open: true,
        loading: false,
        title: this.$t("inspections.bulk.deleteTitle"),
        message: this.$t("inspections.bulk.deleteConfirm", {
          count: ids.length,
        }),
        ids,
      };
    },
    async runBulkDelete() {
      this.bulkConfirm.loading = true;
      try {
        await this.$store.dispatch(
          "bulkDeleteInspections",
          this.bulkConfirm.ids,
        );
        this.bulkConfirm.open = false;
        this.onBulkActionDone();
      } finally {
        this.bulkConfirm.loading = false;
      }
    },
    onBulkActionDone() {
      if (this.$refs.tableComponent) {
        this.$refs.tableComponent.clearSelectItems();
      }
      this.reloadDataFunc();
    },
    newDocument() {
      this.$router.push({
        name: this.tableData.editFormPushName,
        params: {
          id: "1",
          mode: "new",
          data: 1,
          inspectionCategoryId: this.$route.params.inspectionCategoryId,
          inspectionCategoryName: this.$route.params.inspectionCategoryName,
        },
      });
    },
    deleteItem(item) {
      this.$store.dispatch("deleteMultipleInspection", item).then(() => {
        this.reloadDataFunc();
        this.multipleDeleteDialog.open = false;
        this.multipleDeleteDialog.items = [];
      });
    },

    async openMultiUserDialog() {
      try {
        const res = await this.$store.dispatch("getTransferClients");
        this.transferClients = res.data || [];
      } catch (e) {
        return;
      }
      this.multiUserOpen = true;
    },

    async executeTransfer(payload) {
      try {
        const result = await this.$store.dispatch("transferEntities", payload);

        const parts = [];
        if (result.totalSuccess > 0) {
          parts.push(
            this.$t("multiUser.result.success", {
              number: result.totalSuccess,
            }),
          );
        }
        if (result.totalConflict > 0) {
          parts.push(
            this.$t("multiUser.result.error", {
              number: result.totalConflict,
            }),
          );
        }
        if (result.totalError > 0) {
          parts.push(
            this.$t("multiUser.result.error", { number: result.totalError }),
          );
        }

        this.$store.commit("CREATE_SNACKBAR", parts.join(" · "));

        if (payload.action === "move" && result.totalSuccess > 0) {
          this.reloadDataFunc();
        }
      } catch (e) {
        console.error("Transfer failed:", e);
      }
    },
  },
};
</script>
